/* eslint-disable jsx-a11y/anchor-is-valid */
import { ReactNode } from 'react'
import {
  FaUserShield,
  FaShieldHalved,
  FaIdCard,
  FaLocationDot,
  FaCheck,
  FaXmark,
} from 'react-icons/fa6'
import DateComp from '../../../../../components/dateComponent/DateComp'
import { constraintConfig } from '../../../../../constraintConfig'
import labelKey from '../../../../localization/label.json'

type Props = {
  formik: any
  getAllRolesData?: any
}

const emptyDisplay = '—'

/** Card container for each overview section */
const OverviewCard = ({
  icon,
  iconBgClass,
  title,
  badgeText,
  children,
}: {
  icon: ReactNode
  iconBgClass: string
  title: string
  badgeText?: string
  children: ReactNode
}) => (
  <div className='ss-overview-card'>
    <div className='ss-overview-card-header'>
      <div className='ss-overview-card-title'>
        <span className={`ss-overview-icon-badge ${iconBgClass}`}>{icon}</span>
        <span>{title}</span>
      </div>
      {badgeText && <span className='badge badge-light fs-8 fw-semibold text-muted'>{badgeText}</span>}
    </div>
    <div className='ss-overview-card-body'>{children}</div>
  </div>
)

/** Modern compact key-value tile without form input elements */
const OverviewTile = ({
  label,
  value,
  colClass = 'col-sm-6 col-md-4 col-lg-3',
  wrap = false,
}: {
  label: string
  value: any
  colClass?: string
  wrap?: boolean
}) => {
  const isValueEmpty = value === null || value === undefined || value === ''
  const displayVal = isValueEmpty ? emptyDisplay : value

  return (
    <div className={colClass}>
      <div className='ss-overview-tile'>
        <span className='ss-overview-tile-label' title={label}>
          {label}
        </span>
        <span
          className={`ss-overview-tile-value${wrap ? ' text-wrap-break' : ''}${
            isValueEmpty ? ' ss-overview-tile-empty' : ''
          }`}
          title={typeof value === 'string' || typeof value === 'number' ? String(value) : undefined}
        >
          {displayVal}
        </span>
      </div>
    </div>
  )
}

/** Modern status badge tile for boolean permissions */
const PermissionTile = ({
  label,
  checked,
  colClass = 'col-sm-6 col-md-4 col-lg-3',
}: {
  label: string
  checked: boolean
  colClass?: string
}) => (
  <div className={colClass}>
    <div className='ss-overview-tile ss-overview-perm-tile'>
      <span className='ss-overview-tile-label' title={label}>
        {label}
      </span>
      <div className='d-flex align-items-center mt-auto'>
        {checked ? (
          <span className='ss-perm-badge ss-perm-badge-allowed'>
            <FaCheck className='fs-9' /> {labelKey.allowed}
          </span>
        ) : (
          <span className='ss-perm-badge ss-perm-badge-denied'>
            <FaXmark className='fs-9' /> {labelKey.notAllowed}
          </span>
        )}
      </div>
    </div>
  </div>
)

const Step4 = ({ formik }: Props) => {
  const roleId = String(formik.values?.userRole?.roleID ?? '')
  const showManagerBlock =
    roleId !== constraintConfig.roleID.role2.toString() &&
    roleId !== constraintConfig.roleID.role10.toString() &&
    roleId !== constraintConfig.roleID.roleIDPortalSupport.toString()
  const showApprovalLimit = roleId === constraintConfig.roleID.role4.toString()
  const showOrgReports =
    roleId === constraintConfig.roleID.role4.toString() ||
    roleId === constraintConfig.roleID.role10.toString()
  const showExcludeMileage =
    roleId === constraintConfig.roleID.role4.toString() ||
    roleId === constraintConfig.roleID.role5.toString()
  const showUserPermissions =
    roleId === constraintConfig.roleID.role4.toString() ||
    roleId === constraintConfig.roleID.roleExecutive.toString()

  return (
    <>
      <div
        data-kt-stepper-element='content'
        className='overflow-auto overflow-x-hidden update-user-overview'
      >
        <div className='w-100'>
          <div className='update-user-review-header'>
            <div className='d-flex align-items-center gap-2'>
              <h2 className='update-user-review-title'>{labelKey.review}</h2>
              <span className='badge badge-light-primary fw-bold fs-8'>Summary Overview</span>
            </div>
          </div>

          {/* 1. User Role */}
          <OverviewCard
            icon={<FaUserShield className='text-primary' />}
            iconBgClass='bg-light-primary'
            title={labelKey.userRole}
            badgeText='Role & Hierarchy'
          >
            <div className='row g-2'>
              <OverviewTile label={labelKey.role} value={formik.values.userRole?.roleName} />
              <OverviewTile label={labelKey.userType} value={formik.values.userType?.value} />
              <OverviewTile label={labelKey.allowedExpenseType} value={formik.values.expenseType?.value} />
              <OverviewTile label={labelKey.allowedMileageType} value={formik.values.locationType?.value} />

              {showManagerBlock && (
                <>
                  <OverviewTile label={labelKey.manager} value={formik.values?.managerName} />
                  <OverviewTile label={labelKey.delegateManager} value={formik.values?.delegateManager?.value} />
                  <OverviewTile label={labelKey.delegateStartDate} value={formik.values?.delegateStartDate} />
                  <OverviewTile label={labelKey.delegateEndDate} value={formik.values?.delegateEndDate} />
                </>
              )}

              {showApprovalLimit && (
                <OverviewTile label={labelKey.approval$Limit} value={formik.values?.mgrApproveMaxLimit} />
              )}

              <OverviewTile
                label={labelKey.officeLocation}
                value={formik.values?.location?.value}
                colClass='col-sm-12 col-md-8 col-lg-6'
                wrap={true}
              />
              <OverviewTile label={labelKey.department} value={formik.values?.department?.title} />
              <OverviewTile label={labelKey.division} value={formik.values?.division?.title} />
              <OverviewTile label={labelKey.designation} value={formik.values?.designation?.title} />

              {formik.values?.userOrg?.userInOrgName && (
                <OverviewTile label={labelKey.organization} value={formik.values.userOrg.userInOrgName} />
              )}
            </div>
          </OverviewCard>

          {/* 2. User Permissions */}
          <OverviewCard
            icon={<FaShieldHalved className='text-success' />}
            iconBgClass='bg-light-success'
            title={labelKey.userImpersonation}
            badgeText='Access & Privileges'
          >
            <div className='row g-2'>
              <OverviewTile
                label={labelKey.importFromCCFeedDate}
                value={formik.values.ccFeedFrom ? <DateComp formattedDate={formik.values.ccFeedFrom} /> : emptyDisplay}
              />

              {showOrgReports && (
                <PermissionTile label={labelKey.orgReportsPermision} checked={!!formik.values.reportPermision} />
              )}

              <PermissionTile
                label={labelKey.corpCcApprovalQueuePermission}
                checked={!!formik.values.isAllowCorporateCreditCardApprovalQueue}
              />
              <PermissionTile
                label={labelKey.corpCcOrderRequestsPermission}
                checked={!!formik.values.isAllowCorporateCreditCardOrderRequests}
              />

              {showExcludeMileage && (
                <PermissionTile
                  label={labelKey.excludeMileageCalcLogic}
                  checked={!!formik.values.excludeMileageCalculationLogic}
                />
              )}

              {showUserPermissions &&
                formik.values?.userPermission?.map((permission: any, index: number) => (
                  <PermissionTile
                    key={permission?.permissionId ?? permission?.lst_PermissionId ?? index}
                    label={permission?.key || `Permission ${index + 1}`}
                    checked={!!permission?.value}
                  />
                ))}

              <PermissionTile label={labelKey.excludeSupportUser} checked={!!formik.values.excludeSupportUser} />
              <PermissionTile
                label='Allow Impersonate as User'
                checked={!!(formik.values.allowImpersonation || formik.values.isOrgUserImpersonate)}
              />
            </div>

            {(formik.values.allowImpersonation || formik.values.isOrgUserImpersonate) && (
              <div className='mt-3 pt-2 border-top border-gray-200'>
                <p className='ss-overview-tile-label mb-2'>Assigned Impersonation Users</p>
                {formik.values.orgUserImpersonate?.length > 0 ? (
                  <div className='d-flex flex-wrap gap-1'>
                    {formik.values.orgUserImpersonate.map(
                      (imp: { impersonateUserName: string }, index: number) => (
                        <span key={index} className='badge badge-light-primary fw-semibold py-1 px-2' style={{ fontSize: 11 }}>
                          {imp.impersonateUserName}
                        </span>
                      )
                    )}
                  </div>
                ) : (
                  <span className='ss-overview-tile-empty' style={{ fontSize: 12 }}>{emptyDisplay}</span>
                )}
              </div>
            )}
          </OverviewCard>

          {/* 3. User Details */}
          <OverviewCard
            icon={<FaIdCard className='text-warning' />}
            iconBgClass='bg-light-warning'
            title={labelKey.userDetails}
            badgeText='Contact & Identity'
          >
            <div className='row g-2'>
              <OverviewTile label={labelKey.email} value={formik.values?.email} />
              <OverviewTile label={labelKey.employeeId} value={formik.values?.employeeId} />
              <OverviewTile label={labelKey.bcEmployeeId} value={formik.values?.bcEmployeeId} />
              <OverviewTile label={labelKey.firstName} value={formik.values?.firstName} />
              <OverviewTile label={labelKey.middleName} value={formik.values?.middleName} />
              <OverviewTile label={labelKey.lastName} value={formik.values?.lastName} />
              <OverviewTile label={labelKey.cellPhone} value={formik.values?.cellPhone} />
              <OverviewTile label={labelKey.businessPhone} value={formik.values?.businessPhone} />
              <OverviewTile label={labelKey.businessExt} value={formik.values?.businessExt} />
              <OverviewTile label={labelKey.timeZone} value={formik.values.userTimeZone?.title} />
            </div>
          </OverviewCard>

          {/* 4. User Address */}
          <OverviewCard
            icon={<FaLocationDot className='text-danger' />}
            iconBgClass='bg-light-danger'
            title={labelKey.userAddress}
            badgeText='Location & Geo'
          >
            <div className='row g-2'>
              <OverviewTile
                label={labelKey.findAddress}
                value={formik.values?.findAddress}
                colClass='col-12 col-md-6'
                wrap={true}
              />
              <OverviewTile
                label={labelKey.addressLine1}
                value={formik.values?.addressLine1}
                colClass='col-sm-6 col-md-3'
                wrap={true}
              />
              <OverviewTile
                label={labelKey.addressLine2}
                value={formik.values?.addressLine2}
                colClass='col-sm-6 col-md-3'
                wrap={true}
              />
              <OverviewTile label={labelKey.latitude} value={formik.values.latitude} colClass='col-6 col-md-4 col-lg-2' />
              <OverviewTile label={labelKey.longitude} value={formik.values.longitude} colClass='col-6 col-md-4 col-lg-2' />
              <OverviewTile label={labelKey.city} value={formik.values.city} colClass='col-6 col-md-4 col-lg-2' />
              <OverviewTile label={labelKey.state} value={formik.values?.state} colClass='col-6 col-md-4 col-lg-2' />
              <OverviewTile label={labelKey.postalCode} value={formik.values?.postalCode} colClass='col-6 col-md-4 col-lg-2' />
              <OverviewTile label={labelKey.country} value={formik.values.country?.title} colClass='col-6 col-md-4 col-lg-2' />
            </div>
          </OverviewCard>
        </div>
      </div>
    </>
  )
}

export { Step4 }
